import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isType } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialog } from 'components/commonCard/RevealPackCardsDialog';

import type {
  useBuyCardPackMutation,
  useBuyCardPackMutationVariables,
  useBuyCardPack_cardPack,
} from './__generated__/useBuyCardPack.graphql';

const BUY_CARD_PACK_MUTATION = gql`
  mutation useBuyCardPackMutation($input: buyCardPackInput!, $sport: Sport!) {
    buyCardPack(input: $input) {
      currentUser {
        slug
        gemsBalances: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [COMMON_GEM]
        ) {
          amount
        }
      }
      cards {
        slug
        ...RevealPackCardsDialog_anyCardInterface
      }
      pack {
        id
        cardPack {
          slug
          contributesToUnlockTask {
            id
            ... on StarPackTask {
              id
              aasmState
              progress
            }
          }
        }
      }
      errors {
        code
        message
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<useBuyCardPackMutation, useBuyCardPackMutationVariables>;

export const useBuyCardPack = (
  cardPack?: Nullable<useBuyCardPack_cardPack>
) => {
  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();

  const { sport } = useSportContext();
  const { isIosApp, postMessage } = useIsMobileApp();
  const getInteractionContext = useGetInteractionContext();
  const [buyCardPackData, setBuyCardPackData] =
    useState<useBuyCardPackMutation['buyCardPack']>();

  const rewardConfig = cardPack?.contributesToUnlockTask?.rewardConfigs?.[0];
  const starCardPackSlug = isType(rewardConfig, 'CardPacksRewardConfig')
    ? rewardConfig.cardPack?.slug
    : undefined;

  const useNativeIOSPackOpening = isIosApp && !disableNativeIOSPackOpening;

  const [buyCardPack, { loading }] = useMutation(BUY_CARD_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
    update: (cache, { data: mutationData }) => {
      const contributesToUnlockTask =
        mutationData?.buyCardPack?.pack?.cardPack?.contributesToUnlockTask;
      if (!contributesToUnlockTask || !starCardPackSlug) {
        return;
      }
      // update star pack rewardedByTask field if it exists
      // this is needed because initially rewardedByTask is a template
      // and the id will change when it becomes a real task
      cache.modify({
        id: cache.identify({
          slug: starCardPackSlug,
          __typename: 'CardPack',
        }),
        fields: {
          rewardedByTask: () =>
            mutationData?.buyCardPack?.pack?.cardPack
              ?.contributesToUnlockTask || undefined,
        },
      });
    },
  });

  const onPurchase = async () => {
    const { data } = await buyCardPack({
      variables: {
        input: {
          cardPackSlug: cardPack?.slug || '',
          sport,
          interactionContext: getInteractionContext(),
        },
        sport,
      },
    });
    setBuyCardPackData(data?.buyCardPack);
  };

  const onMobilePurchase = () => {
    postMessage('buyCardPack', {
      packId: cardPack?.slug,
    });
  };
  const packNotBought =
    buyCardPackData?.cards?.length === 0 || !buyCardPackData?.pack?.id;

  const onCloseRevealDialog = () => {
    setBuyCardPackData(undefined);
  };
  return {
    buyCardPackData,
    onPurchase,
    onMobilePurchase,
    useNativeIOSPackOpening,
    packNotBought,
    onCloseRevealDialog,
    loading,
  };
};

useBuyCardPack.fragments = {
  cardPack: gql`
    fragment useBuyCardPack_cardPack on CardPack {
      slug
      contributesToUnlockTask {
        id
        rewardConfigs {
          id
          ... on CardPacksRewardConfig {
            id
            cardPack {
              slug
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<useBuyCardPack_cardPack>,
};
